import {DOCUMENT_FIELDS_PATH, type EntityOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import {updateProjectItemFieldValueMutation as queryId} from '@github-ui/server-persisted-queries'
import type {UpdateProjectItemFieldValueMutationMutation} from '@github-ui/server-persisted-queries/types/updateProjectItemFieldValueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {
  applyProjectItemFieldValue,
  type OptimisticProjectItemFieldValue,
  projectItemFieldValueFromResponse,
  upsertProjectItemFieldValue,
} from './project-item-field-value-optimistic'

type UpsertProjectItemFieldValueOperation = Extract<EntityOperation, {type: 'upsertEntityFieldItem'}>
type MutationContext = {relayUpdate?: Disposable}

export type UpdateProjectItemFieldValue = (fieldId: string, value: OptimisticProjectItemFieldValue) => void

type ProjectItemFieldValueInput =
  | {text: string}
  | {number: number | null}
  | {date: string}
  | {singleSelectOptionId: string}
  | {iterationId: string}
  | {multiSelectOptionIds: string[]}

function inputValueFromOperation(operation: UpsertProjectItemFieldValueOperation): ProjectItemFieldValueInput {
  const value = operation.item

  if (typeof value.text === 'string') return {text: value.text}
  if (typeof value.number === 'number' || value.number === null) return {number: value.number}
  if (typeof value.date === 'string') return {date: value.date}
  if (typeof value.optionId === 'string') return {singleSelectOptionId: value.optionId}
  if (typeof value.iterationId === 'string') return {iterationId: value.iterationId}
  if (Array.isArray(value.options)) {
    const multiSelectOptionIds = value.options.flatMap(option =>
      option && typeof option === 'object' && typeof option.id === 'string' ? [option.id] : [],
    )
    if (multiSelectOptionIds.length === value.options.length) return {multiSelectOptionIds}
  }

  throw new Error('Project item field value is not supported')
}

function updateRelayFieldValue(
  store: RecordSourceProxy,
  itemId: string,
  fieldName: string,
  value: OptimisticProjectItemFieldValue,
): void {
  const fieldValue = store.get(value.id) ?? store.create(value.id, 'ProjectV2ItemFieldSingleSelectValue')
  fieldValue.setValue(value.id, 'id')

  for (const key of ['optionId', 'name', 'nameHTML', 'color'] as const) {
    const fieldValueProperty = value[key]
    if (typeof fieldValueProperty === 'string' || fieldValueProperty === null) {
      fieldValue.setValue(fieldValueProperty, key)
    }
  }

  store.get(itemId)?.setLinkedRecord(fieldValue, 'fieldValueByName', {name: fieldName})
}

async function updateProjectItemFieldValueRequest({
  owner,
  repo,
  issueNumber,
  operation,
}: {
  owner: string
  repo: string
  issueNumber: number
  operation: UpsertProjectItemFieldValueOperation
}): Promise<UpdateProjectItemFieldValueMutationMutation> {
  const query = new URLSearchParams({queryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/project_item.json?${query}`,
    {
      method: 'PATCH',
      body: {itemId: operation.id, fieldId: operation.key, value: inputValueFromOperation(operation)},
    },
  )

  let result:
    | {data?: UpdateProjectItemFieldValueMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const projectItem = data?.updateProjectV2ItemFieldValue?.projectV2Item
  const confirmedValue = projectItemFieldValueFromResponse(projectItem?.fieldValues, operation.key)

  if (!response.ok || !data || projectItem?.id !== operation.id || !confirmedValue) {
    throw new Error(error ?? 'Failed to update project item field value')
  }

  return data
}

export function useUpdateProjectItemFieldValueMutation({
  owner,
  repo,
  issueNumber,
  projectItemId,
  queryKey,
  additionalQueryKey,
  relayFieldName,
  onCompleted,
  onError,
}: {
  owner: string
  repo: string
  issueNumber: number
  projectItemId: string
  queryKey: QueryKey
  additionalQueryKey?: QueryKey
  relayFieldName?: string
  onCompleted?: (response: UpdateProjectItemFieldValueMutationMutation) => void
  onError?: (error: Error) => void
}) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const {mutate} = useConnectionMutation<UpdateProjectItemFieldValueMutationMutation, Error, MutationContext>({
    ownerId: ENTITY_FIELDS_OWNER,
    connectionPath: DOCUMENT_FIELDS_PATH,
    scope: {id: `update-project-item-field-value:${projectItemId}`},
    mutationFn: operation =>
      updateProjectItemFieldValueRequest({
        owner,
        repo,
        issueNumber,
        operation: operation as UpsertProjectItemFieldValueOperation,
      }),
    // RELAY-CLEANUP(ProjectsSectionQuery): mirror Status until the collapsed project row reads TanStack data.
    onMutate: ({operation}) => {
      const updateOperation = operation as UpsertProjectItemFieldValueOperation
      return {
        relayUpdate: relayFieldName
          ? environment.applyUpdate({
              storeUpdater: store =>
                updateRelayFieldValue(
                  store,
                  projectItemId,
                  relayFieldName,
                  updateOperation.item as OptimisticProjectItemFieldValue,
                ),
            })
          : undefined,
      }
    },
    onSuccess: async (data, {operation}, context) => {
      try {
        const updateOperation = operation as UpsertProjectItemFieldValueOperation
        const projectItem = data.updateProjectV2ItemFieldValue?.projectV2Item
        const confirmedValue = projectItemFieldValueFromResponse(projectItem?.fieldValues, updateOperation.key)
        if (!confirmedValue) throw new Error('Failed to update project item field value')

        if (relayFieldName) {
          commitLocalUpdate(environment, store =>
            updateRelayFieldValue(store, projectItemId, relayFieldName, confirmedValue),
          )
        }
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey}, ...(additionalQueryKey ? [{queryKey: additionalQueryKey}] : [])],
          ownerId: ENTITY_FIELDS_OWNER,
          operation: upsertProjectItemFieldValue(projectItemId, updateOperation.key, confirmedValue),
          applyEntityFieldItem: applyProjectItemFieldValue,
        })
        onCompleted?.(data)
      } finally {
        context?.relayUpdate?.dispose()
      }
    },
    onError: (error, _variables, context) => {
      context?.relayUpdate?.dispose()
      onError?.(error)
    },
  })

  return useCallback<UpdateProjectItemFieldValue>(
    (fieldId, value) => mutate({operation: upsertProjectItemFieldValue(projectItemId, fieldId, value)}),
    [mutate, projectItemId],
  )
}
